// React Query hooks + helpers for /settings/push.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useToast } from '../../../hooks/useToast';

export interface StaffDevice {
  uuid: string;
  platform: string;
  device_info: string | null;
  is_active: boolean;
  last_used_at: string | null;
  created_at: string;
}

export const PUSH_DEVICES_KEY = ['devices', 'me'] as const;

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function usePushPermission(): NotificationPermission | 'unsupported' {
  const [perm, setPerm] = useState<NotificationPermission | 'unsupported'>(
    () => (isPushSupported() ? Notification.permission : 'unsupported'),
  );
  useEffect(() => {
    if (!isPushSupported()) return;
    const t = window.setInterval(() => {
      setPerm(Notification.permission);
    }, 2000);
    return () => window.clearInterval(t);
  }, []);
  return perm;
}

export function useMyDevices() {
  return useQuery<StaffDevice[]>({
    queryKey: PUSH_DEVICES_KEY,
    queryFn: async () => {
      const res = await apiClient.get<{ devices: StaffDevice[] }>(
        endpoints.devices.list,
      );
      return res.data.devices;
    },
    staleTime: 30_000,
  });
}

interface SubscribePayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  user_agent?: string;
}

export function useRegisterDevice() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (payload: SubscribePayload) => {
      const res = await apiClient.post<
        { uuid: string; is_active: boolean; platform: string; updated: boolean },
        SubscribePayload
      >(endpoints.devices.register, payload);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PUSH_DEVICES_KEY });
      toast.success('Push notifications enabled.');
    },
    onError: () => {
      toast.error('Failed to register device.');
    },
  });
}

export function useUnregisterDevice() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (endpoint: string) => {
      await apiClient.delete(endpoints.devices.revoke, {
        data: { endpoint },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PUSH_DEVICES_KEY });
      toast.success('Push notifications disabled on this browser.');
    },
    onError: () => {
      toast.error('Failed to revoke device.');
    },
  });
}

function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const padded = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out.buffer.slice(0);
}

function arrayBufferToBase64(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] ?? 0);
  return window.btoa(s);
}

export interface SerialisedSubscription {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export function serialiseSubscription(sub: PushSubscription): SerialisedSubscription {
  return {
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
      auth: arrayBufferToBase64(sub.getKey('auth')),
    },
  };
}

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg) return reg;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

export async function subscribePush(): Promise<SerialisedSubscription | null> {
  if (!isPushSupported()) return null;
  const vapid = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  const reg = await ensureServiceWorker();
  if (!reg) return null;
  if (Notification.permission !== 'granted') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') return null;
  }
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    return serialiseSubscription(existing);
  }
  if (!vapid) return null;
  const fresh = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapid),
  });
  return serialiseSubscription(fresh);
}

export async function unsubscribePush(): Promise<string | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}
